// src/offline/noteUtils.ts
import { type NoteQueued, type NoteSynced } from "@/db";

const STATUS_LABELS: Record<string, string> = {
  INTERESTED: "Interested, needs quote",
  REGULAR: "Agreed to regular clean",
  CUSTOMER: "Already a customer",
  LEAFLET: "Left leaflet",
  CALLBACK: "Callback requested",
  NOT_INTERESTED: "Not interested",
  OBSERVED_DIRTY: "Observed dirty windows",
  QUOTED: "Quoted",
  EXCLUDE_LOW_DEM: "Excluded - Low Demographic",
  EXCLUDE_ACCESS: "Exclude - Access Issues",
  OTHER: "Other",
};

export function statusDisplay(status?: string | null, fallback?: string | null) {
  if (!status) return fallback ?? "";
  return STATUS_LABELS[status] ?? fallback ?? status;
}

export function formatTimestamp(ts?: string | null) {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

type NoteInput = Partial<NoteSynced> &
  Partial<NoteQueued> & {
    pending?: boolean;
    client_created_at?: string;
    username?: string | null;
    status_display?: string | null;
  };

export function formatNoteForDisplay(note: NoteInput, currentUsername?: string | null) {
  const created_at =
    note.created_at ??
    note.created_at_client ??
    note.client_created_at ??
    null;
  return {
    ...note,
    status_display: statusDisplay(note.status, note.status_display),
    username: note.username ?? currentUsername ?? "You (offline)",
    pending: !!note.pending,
    created_at,
  };
}

export function mergeNotes(
  syncedNotes: NoteSynced[],
  queuedNotes: NoteQueued[],
  currentUsername?: string | null
) {
  const formattedSynced = syncedNotes.map((n) =>
    formatNoteForDisplay({ ...n, pending: false }, currentUsername)
  );

  const formattedQueued = queuedNotes.map((n) =>
    formatNoteForDisplay(
      {
        ...(n.payload?.body ?? {}),
        pending: true,
        created_at: n.created_at_client ?? (n as any).created_at ?? n.payload?.body?.client_created_at,
        client_created_at: n.payload?.body?.client_created_at,
        username: n.payload?.body?.username ?? currentUsername,
      },
      currentUsername
    )
  );

  const all = [...formattedSynced, ...formattedQueued].sort((a, b) => {
    const tsA = String(a.created_at ?? "");
    const tsB = String(b.created_at ?? "");
    return tsB.localeCompare(tsA);
  });

  return {
    latest_note: all[0] ?? null,
    notes: all,
  };
}
